import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  AreaChart, Area, ScatterChart, Scatter, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { BarChart2, TrendingUp, Circle, Layers, PieChart as PieIcon, X } from 'lucide-react';

const COLORS = ['#5bb8f5', '#e8884a', '#5cb85c', '#d4a843', '#9b8ef0', '#e05252', '#60c6c0', '#c478d4'];

const CHART_TYPES = [
  { id: 'bar',     label: 'Bar',     Icon: BarChart2  },
  { id: 'line',    label: 'Line',    Icon: TrendingUp },
  { id: 'area',    label: 'Area',    Icon: Layers     },
  { id: 'scatter', label: 'Scatter', Icon: Circle     },
  { id: 'pie',     label: 'Pie',     Icon: PieIcon    },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border2)',
      borderRadius: 4, padding: '8px 12px', fontFamily: 'var(--sans)', fontSize: 12,
    }}>
      {label !== undefined && <div style={{ color: 'var(--text2)', marginBottom: 4 }}>{String(label)}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: 'var(--text)', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block', flexShrink: 0 }} />
          <span style={{ color: 'var(--text2)' }}>{p.name}:</span>
          <span>{typeof p.value === 'number' ? p.value.toLocaleString(undefined, { maximumFractionDigits: 4 }) : p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function ChartPanel({ rows, columns, onClose }) {
  const numericCols = useMemo(() =>
    columns.filter(c => rows.length > 0 && typeof rows[0][c] === 'number'), [rows, columns]);
  const allCols = columns;

  const [chartType, setChartType] = useState('bar');
  const [xAxis, setXAxis] = useState(allCols[0] || '');
  const [yAxes, setYAxes] = useState(numericCols.slice(0, 1));

  const selectStyle = {
    background: 'var(--bg3)', border: '1px solid var(--border)',
    borderRadius: 4, padding: '4px 8px', color: 'var(--text)',
    fontFamily: 'var(--sans)', fontSize: 12, outline: 'none', cursor: 'pointer',
  };

  const toggleY = (col) => {
    setYAxes(prev => prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]);
  };

  const chartData = rows.slice(0, 500);
  const axisProps = {
    stroke: 'var(--border)',
    tick: { fill: 'var(--text3)', fontSize: 11, fontFamily: 'var(--sans)' },
  };

  const renderChart = () => {
    const commonProps = { data: chartData, margin: { top: 10, right: 16, left: 0, bottom: 40 } };

    if (chartType === 'pie' && yAxes[0]) {
      const pieData = chartData.slice(0, 20).map(row => ({
        name: String(row[xAxis] ?? ''),
        value: Number(row[yAxes[0]] ?? 0),
      }));
      return (
        <PieChart>
          <Pie data={pieData} cx="50%" cy="50%" outerRadius={140} dataKey="value" nameKey="name"
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
            {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      );
    }

    if (chartType === 'scatter' && yAxes[0]) {
      return (
        <ScatterChart {...commonProps}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis dataKey={xAxis} {...axisProps} angle={-35} textAnchor="end" />
          <YAxis {...axisProps} />
          <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
          <Scatter name={yAxes[0]} data={chartData.map(r => ({ [xAxis]: r[xAxis], [yAxes[0]]: r[yAxes[0]] }))} fill={COLORS[0]} />
        </ScatterChart>
      );
    }

    const ChartComponent = chartType === 'line' ? LineChart : chartType === 'area' ? AreaChart : BarChart;
    return (
      <ChartComponent {...commonProps}>
        <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey={xAxis} {...axisProps} angle={-35} textAnchor="end" interval="preserveStartEnd" />
        <YAxis {...axisProps} />
        <Tooltip content={<CustomTooltip />} />
        {yAxes.length > 1 && <Legend wrapperStyle={{ fontFamily: 'var(--sans)', fontSize: 11, color: 'var(--text2)' }} />}
        {yAxes.map((col, i) => {
          const color = COLORS[i % COLORS.length];
          if (chartType === 'line') return <Line key={col} type="monotone" dataKey={col} stroke={color} dot={false} strokeWidth={2} />;
          if (chartType === 'area') return <Area key={col} type="monotone" dataKey={col} stroke={color} fill={color} fillOpacity={0.12} strokeWidth={2} />;
          return <Bar key={col} dataKey={col} fill={color} radius={[2, 2, 0, 0]} />;
        })}
      </ChartComponent>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        padding: '5px 12px', background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}>
        {CHART_TYPES.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setChartType(id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 4,
              border: `1px solid ${chartType === id ? 'var(--accent)' : 'var(--border)'}`,
              background: chartType === id ? 'rgba(91,184,245,0.1)' : 'transparent',
              color: chartType === id ? 'var(--accent)' : 'var(--text2)',
              fontFamily: 'var(--sans)', fontSize: 12, cursor: 'pointer',
              transition: 'all 0.1s',
            }}
          >
            <Icon size={11} />{label}
          </button>
        ))}

        <div style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 2px' }} />

        <span style={{ fontSize: 12, color: 'var(--text3)' }}>X:</span>
        <select value={xAxis} onChange={e => setXAxis(e.target.value)} style={selectStyle}>
          {allCols.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <span style={{ fontSize: 12, color: 'var(--text3)' }}>Y:</span>
        {numericCols.map((col, i) => (
          <button
            key={col}
            onClick={() => toggleY(col)}
            style={{
              padding: '3px 8px', borderRadius: 4,
              border: `1px solid ${yAxes.includes(col) ? COLORS[i % COLORS.length] : 'var(--border)'}`,
              background: yAxes.includes(col) ? `${COLORS[i % COLORS.length]}18` : 'transparent',
              color: yAxes.includes(col) ? COLORS[i % COLORS.length] : 'var(--text2)',
              fontFamily: 'var(--sans)', fontSize: 12, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
              transition: 'all 0.1s',
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: COLORS[i % COLORS.length] }} />
            {col}
          </button>
        ))}

        <div style={{ flex: 1 }} />
        {rows.length > 500 && <span style={{ fontSize: 11, color: 'var(--text3)' }}>First 500 rows</span>}
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: 'var(--text3)',
          cursor: 'pointer', padding: 3, display: 'flex',
          borderRadius: 3, transition: 'color 0.1s',
        }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
        >
          <X size={14} />
        </button>
      </div>

      {/* Chart */}
      <div style={{ flex: 1, padding: '8px 4px 0', overflow: 'hidden' }}>
        {yAxes.length === 0
          ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text3)', fontSize: 12 }}>
              Select at least one Y column
            </div>
          : <ResponsiveContainer width="100%" height="100%">{renderChart()}</ResponsiveContainer>
        }
      </div>
    </div>
  );
}
